import { useGSAP } from '@gsap/react';
import { graphql, useStaticQuery } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS } from '../../../constants';
import { ButtonLinkType } from '../../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { BREAKPOINTS } from '../../../styles/breakpoints';
import { AreaOfExpertise } from '../../../templates/AreaOfExpertisePage';
import { ModuleBgColor } from '../../../templates/Page';
import { Service } from '../../../templates/ServicePage';
import { getAreasOfExpertiseAsBlocks, getServicesAsBlocks } from '../../../utils/projectUtils';
import { getUrlFromVersatileLink, localize } from '../../../utils/sanity';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import SmartLink from '../SmartLink';
import TextLink from '../TextLink';
import * as styles from './TextBlocksModule.module.scss';

export const TextBlocksModuleFragment = graphql`
  fragment TextBlocksModule on SanityPageTextBlocksModule {
    title {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    prepopulateContentWith
    blocks {
      title {
        ...LocaleStringFragment
      }
      text {
        ...LocaleTextFragment
      }
      link {
        ...ButtonLink
      }
    }
    backgroundImage {
      ...SanityImage
    }
  }
`;

export interface TextBlock {
  title: string;
  text: string;
  link?: ButtonLinkType;
}

export interface TextBlocksModuleProps {
  title: string;
  text?: string;
  prepopulateContentWith?: 'areasOfExpertise' | 'services' | 'none';
  blocks: Array<TextBlock>;
  backgroundImage: SanityImageType;
  className?: string;
}

interface QueryData {
  allSanityAreaOfExpertise: {
    nodes: Array<AreaOfExpertise>;
  };
  allSanityService: {
    nodes: Array<Service>;
  };
}

export function getModuleBgColor(props: TextBlocksModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'colored';
}

function TextBlocksModule(props: TextBlocksModuleProps & CommonModuleProps): React.ReactElement {
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      allSanityAreaOfExpertise(sort: { orderRank: ASC }) {
        nodes {
          ...AreaOfExpertise
        }
      }
      allSanityService(sort: { orderRank: ASC }) {
        nodes {
          ...Service
        }
      }
    }
  `);

  const {
    title,
    text,
    prepopulateContentWith,
    blocks,
    backgroundImage,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const { allSanityAreaOfExpertise, allSanityService } = localizedData;

  const containerRef = useRef<HTMLElement | null>(null);

  const areasOfExpertiseAsTextBlocks = getAreasOfExpertiseAsBlocks(
    allSanityAreaOfExpertise.nodes,
  ) as Array<TextBlock>;
  const servicesAsTextBlocks = getServicesAsBlocks(allSanityService.nodes) as Array<TextBlock>;

  const textBlocks =
    (prepopulateContentWith === 'areasOfExpertise' && areasOfExpertiseAsTextBlocks) ||
    (prepopulateContentWith === 'services' && servicesAsTextBlocks) ||
    blocks;

  const evenIndexArrayBlocks = textBlocks.filter((_, index) => index % 2 === 0);
  const oddIndexBlocks = textBlocks.filter((_, index) => index % 2 !== 0);

  useGSAP(
    () => {
      const desktopLeftTextBlocks = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.desktop} .${styles.textOuterWrapper}.${styles.leftBranch}`,
      );

      const desktopRightTextBlocks = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.desktop} .${styles.textOuterWrapper}.${styles.rightBranch}`,
      );

      const desktopTextBlocks: Array<HTMLDivElement> = [];
      for (let i = 0; i < desktopLeftTextBlocks.length; i++) {
        desktopTextBlocks.push(desktopLeftTextBlocks[i]);
        if (i < desktopRightTextBlocks.length) {
          desktopTextBlocks.push(desktopRightTextBlocks[i]);
        }
      }

      const mobileTextBlocks = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.blocks}.${styles.mobile} .${styles.textOuterWrapper}`,
      );

      [desktopTextBlocks, mobileTextBlocks].forEach(textBlocks => {
        textBlocks.forEach((target, i) => {
          const isLeftSide = target.classList.contains(styles.leftBranch);
          const tl = gsap.timeline({
            delay: isLeftSide ? 0 : 0.5,
            scrollTrigger: {
              trigger: target,
              start: 'top 85%',
            } satisfies ScrollTrigger.Vars,
          });

          tl.set(target, {
            transition: 'none',
            immediateRender: true,
          });

          // entrance
          tl.from(target, {
            opacity: 0,
            y: -20,
            duration: 0.4,
            stagger: 2,
            clearProps: 'transition,transform',
          });
        });
      });
    },
    { scope: containerRef },
  );

  function renderTextBlock(textBlock: TextBlock) {
    return (
      <div className={styles.blockContainer}>
        <h3 className={styles.blockTitle}>{textBlock.title}</h3>
        <p className={styles.blockText}>{textBlock.text}</p>
        {textBlock.link && (
          <TextLink styleOnly className={styles.link} withArrowIcon>
            {textBlock.link.text}
          </TextLink>
        )}
      </div>
    );
  }

  function renderTextBlocks(blocks: Array<TextBlock>, className?: string) {
    return blocks.map((block, i) => (
      <div key={i} className={clsx(styles.textOuterWrapper, className)}>
        {block.link ? (
          <SmartLink
            key={i}
            to={getUrlFromVersatileLink(block.link)}
            className={clsx(styles.blockLink)}
          >
            {renderTextBlock(block)}
          </SmartLink>
        ) : (
          <React.Fragment key={i}>{renderTextBlock(block)}</React.Fragment>
        )}
      </div>
    ));
  }

  return (
    <ModuleLayout
      id={moduleId}
      className={clsx(className, styles.container)}
      contentClassName={clsx(styles.contentContainer)}
      childrenClassName={styles.content}
      title={title}
      text={text}
      moduleRef={containerRef}
      childrenWithoutHorizontalPadding
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
    >
      <div className={styles.contentWrapper}>
        <div className={clsx(styles.blocks, styles.desktop)}>
          {renderTextBlocks(evenIndexArrayBlocks, styles.leftBranch)}
        </div>

        <div className={styles.arc}></div>
        <div className={clsx(styles.blocks, styles.desktop)}>
          {renderTextBlocks(oddIndexBlocks, styles.rightBranch)}
        </div>
        <div className={styles.backgroundImageContainer}>
          <Image
            className={styles.backgroundImage}
            image={backgroundImage}
            dimensions={[
              [520, 1080],
              [BREAKPOINTS.laptop, 1920, 1080],
            ]}
          />
        </div>
        <div className={clsx(styles.blocks, styles.mobile)}>{renderTextBlocks(textBlocks)}</div>
      </div>
    </ModuleLayout>
  );
}

export default TextBlocksModule;
